import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Dumbbell, RefreshCw, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

function renderMarkdown(text) {
  if (!text) return null;
  // very small md: **bold** and bullet lines
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  return lines.map((l, i) => {
    const isBullet = /^\s*[-•*]/.test(l);
    const cleaned = l.replace(/^\s*[-•*]\s?/, "");
    const parts = cleaned.split(/(\*\*[^*]+\*\*)/g);
    return (
      <div key={i} className={`flex gap-2 ${isBullet ? "items-start" : ""}`}>
        {isBullet && <span className="text-indigo-400 mt-1.5 shrink-0">•</span>}
        <span className="leading-relaxed">
          {parts.map((p, j) =>
            p.startsWith("**") && p.endsWith("**") ? (
              <strong key={j} className="text-indigo-200 font-semibold">{p.slice(2, -2)}</strong>
            ) : (
              <span key={j}>{p}</span>
            )
          )}
        </span>
      </div>
    );
  });
}

export default function ExerciseRecommendations() {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    http.get("/ai/exercise").then((r) => setText(r.data?.content || "")).finally(() => setLoading(false));
  };
  useEffect(load, []);

  return (
    <section className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Dumbbell className="w-6 h-6 text-emerald-300" /> Exercise, tuned to your shifts
          </h2>
          <p className="text-slate-400 text-sm mt-1">When and how to train so your body sleeps deeper — powered by Claude Sonnet 5.</p>
        </div>
        <Button
          onClick={load}
          disabled={loading}
          data-testid="btn-regenerate-exercise"
          variant="outline"
          className="rounded-full bg-transparent border-white/15 text-slate-200 hover:bg-white/5"
        >
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Regenerate
        </Button>
      </div>

      <div className="circa-card p-6 sm:p-8" data-testid="exercise-content">
        <div className="pill text-emerald-300 border-emerald-500/30 bg-emerald-950/50 mb-5">
          <Sparkles className="w-3.5 h-3.5" /> Personalized · Next 48h
        </div>
        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-4 w-3/4 bg-white/10" />
            <Skeleton className="h-4 w-2/3 bg-white/10" />
            <Skeleton className="h-4 w-4/5 bg-white/10" />
            <Skeleton className="h-4 w-1/2 bg-white/10" />
          </div>
        ) : (
          <div className="space-y-3 text-slate-200 text-sm sm:text-base">
            {renderMarkdown(text)}
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <QuickTip title="Post-shift" body="Light mobility only. No hard cardio within 2h of anchor sleep." />
        <QuickTip title="Wake window" body="15-min brisk walk in daylight anchors your circadian rhythm." />
        <QuickTip title="Off days" body="Bank one moderate strength session before 6pm." />
      </div>
    </section>
  );
}

function QuickTip({ title, body }) {
  return (
    <div className="circa-card p-5">
      <div className="text-xs uppercase tracking-widest font-mono-ui text-emerald-300/80 mb-2">{title}</div>
      <div className="text-slate-200 text-sm leading-relaxed">{body}</div>
    </div>
  );
}
